'use client'

import { useContext, useState } from 'react'
import slugify from 'slugify'
import NextImage from '@components/next-image'
import useAnalytics, { type SPEvent, type SPContext } from '@hooks/use-analytics'
import useCollapsibleAnalytics from '@hooks/use-collapsible-analytics'
import useWindowSize from '@hooks/use-window-size'
import { BlockContext } from '@lib/analytics'
import { getIconFromContentful } from '@lib/icons'
import { pyMap } from '@lib/token-maps'
import type { TypeCardAccordionBlockFields, TypeCardAccordionItemFields } from '@lib/generated-types'
import { routeMapper } from '@lib/menu-item-mapper'
import { richTextRender } from '@lib/rich-text-base'
import { CardAccordion, CardAccordionBlock as Block } from '@shc/ui'

const CardAccordionBlock = ({
  sectionLink,
  cards,
  paddingTop,
  paddingBottom,
  cardAlignment,
}: TypeCardAccordionBlockFields) => {
  const { track } = useAnalytics()
  const blockContext = useContext(BlockContext)
  const [eventName, setEventName] = useState('')
  const [handleCollapsibleAnalytics] = useCollapsibleAnalytics()
  const { width } = useWindowSize() as any

  const handleBtnAnalytics = (label: string | undefined, link: string, fields: any, sys: any) => {
    track({
      event: {
        name: 'component_click',
        data: {}
      } as SPEvent,
      contexts: [
        {
          name: 'component',
          data: {
            component_text: label,
            component_url: link,
          },
        },
        blockContext,
        {
          name: 'content',
          data: {
            content_type: sys.contentType.sys.id,
            content_internal_name: fields.heading,
            content_entry_id: sys.id,
            content_level: 'item',
          },
        }
      ] as SPContext[],
    })
  }

  const handleAccordionAnalytics = (label: string | null, fields: any, sys: any) => {
    const contexts: SPContext[] = [
      {
        name: 'component',
        data: {
          component_text: label,
        },
      },
      {
        name: 'content',
        data: {
          content_type: sys.contentType.sys.id,
          content_internal_name: fields.heading,
          content_entry_id: sys.id,
          content_level: 'item',
        },
      }
    ]

    if (blockContext) contexts.push(blockContext)

    if (eventName === '' || eventName === 'component_collapse') {
      setEventName('component_expand')

      track({
        event: { name: 'component_expand', data: {} } as SPEvent,
        contexts,
      })
    } else {
      setEventName('component_collapse')

      track({
        event: { name: 'component_collapse', data: {} } as SPEvent,
        contexts,
      })
    }
  }

  return (
    <Block
      cardAlignment={cardAlignment}
      id={typeof sectionLink === 'undefined' ? undefined : slugify(sectionLink)}
      paddingBottom={pyMap[paddingBottom]}
      paddingTop={pyMap[paddingTop]}>
      {cards.map(({ fields, sys }, index) => {
        const fieldsData = fields as TypeCardAccordionItemFields
        const btnLink = routeMapper(fieldsData?.buttonLink, fieldsData?.buttonAnchorOrExternalLink)
        const imageFields = fieldsData?.image?.fields?.image.fields

        return (
          <CardAccordion
            button={(fieldsData?.buttonLink || fieldsData?.buttonAnchorOrExternalLink) && fieldsData?.buttonLabel ? {
              href: btnLink.route,
              label: fieldsData?.buttonLabel,
              onClick: () => handleBtnAnalytics(fieldsData?.buttonLabel, btnLink.route, fields, sys),
              rel: btnLink.isInternal ? undefined : 'noopener noreferrer',
              target: btnLink.isInternal ? undefined : '_blank',
            } : undefined }
            className="card-accordion-item"
            collapsibleTitle={fieldsData?.accordionLabel}
            decorator={fieldsData?.decorator}
            description={fieldsData?.description}
            heading={fieldsData?.heading}
            key={index}
            labels={fieldsData?.labels?.map(label => (
              {
                children: label.fields.text,
                icon: getIconFromContentful(label.fields.icon)
              }
            ))}
            preHeading={fieldsData?.featuredText}
            slots={{
              collapsibleContainer: 'div',
              image: NextImage
            }}
            slotProps={{
              collapsibleContainer: {
                onClick: () => {
                  width < 640 ? handleCollapsibleAnalytics(fieldsData?.accordionLabel, fields, sys) : handleAccordionAnalytics(fieldsData?.accordionLabel, fields, sys)
                }
              },
              image: imageFields && {
                alt: imageFields.title,
                height: 120,
                src: imageFields.file.url,
                width: 120,
              }
            }}>
            <>{richTextRender(fieldsData?.accordionBody)}</>
          </CardAccordion>
        )
      })}
    </Block>
  )
}

export default CardAccordionBlock
